import { useQuery } from '@tanstack/react-query'
import { type Me, meQuery } from './me'

/**
 * This file is the stable seam plan 47 must not rewrite.
 */

export type MinScope = 'own' | 'department' | 'company'

export interface VisibilityTarget {
  /**
   * Module slug from /me's enabled_modules. Absent = always licensed.
   *
   * A LIST means "any one of these", not all of them. Directory is why it
   * exists: it holds the master data that Accounting, Sales, Purchasing and
   * Inventory all read, and it has no licence switch of its own. An HR-only
   * tenant has no use for a customer or a product, so the section is derived
   * from its consumers instead. An EMPTY list denies, which is the safe end.
   */
  module?: ModuleRequirement
  /** Permission key. Absent = no permission needed. */
  permission?: string
  /** Lowest data scope that may see this. Defaults to 'own'. */
  minScope?: MinScope
}

export type ModuleRequirement = string | readonly string[]

/**
 * Every data scope the API serves (ADR 0035). A screen's `minScope` asks a
 * question of meaning, not of order: `department` means "reaches other
 * people", which branch and both combined scopes do; `company` means the whole
 * company. A numeric rank cannot say this, because department and branch do
 * not contain each other; the old table left branch out and hid HR screens
 * from branch managers.
 */
const KNOWN_SCOPES = new Set([
  'own',
  'department_in_branch',
  'department',
  'branch',
  'department_or_branch',
  'company',
])

function meetsMinScope(scope: string, minScope: MinScope): boolean {
  if (!KNOWN_SCOPES.has(scope)) return false
  if (minScope === 'company') return scope === 'company'
  if (minScope === 'department') return scope !== 'own'
  return true
}

function licensed(enabled: Set<string>, required: ModuleRequirement): boolean {
  return typeof required === 'string'
    ? enabled.has(required)
    : required.some((slug) => enabled.has(slug))
}

export function canSeeWith(me: Me | undefined, target: VisibilityTarget): boolean {
  if (!target.module && !target.permission) return true
  if (!me) return false

  const modules = new Set<string>(Array.isArray(me.enabled_modules) ? me.enabled_modules : [])
  if (target.module && !licensed(modules, target.module)) return false
  if (!target.permission) return true

  const grants = new Map<string, string>(
    me.effective_grants.map((grant) => [grant.key, grant.scope]),
  )
  const scope = grants.get(target.permission)
  if (!scope) return false

  return meetsMinScope(scope, target.minScope ?? 'own')
}

/**
 * Whether this session may do nothing but self-service.
 *
 * An EMPTY grant list is deliberately NOT self-service only. A session that
 * has not yet chosen a company answers `/me` with no grants at all
 * (`ensureActiveCompany` in `lib/me.ts` exists because of it), and treating
 * that shape as own-scope would wall every user on their first request.
 */
export function isSelfServiceOnly(me: Me | undefined): boolean {
  const grants = me?.effective_grants ?? []
  return grants.length > 0 && grants.every((grant) => grant.scope === 'own')
}

export function useRailVisibility(): { canSee: (target: VisibilityTarget) => boolean } {
  const { data: me } = useQuery(meQuery)
  return { canSee: (target) => canSeeWith(me, target) }
}
